import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { useGetProduct } from "../../hooks/productHooks";
import { Loader } from "../../components/Loader";
import { imagesUrl } from "../../services/endpoints";
import { useCallback, useEffect } from "react";
import { useSnackBar } from "../../context/SnackbarContext";
import Cropper, { Area, Point } from "react-easy-crop";
import React from "react";
import { getCroppedImg, readFile } from "../../utils/util";
import { ErrorRecord, Product } from "../../types";
import { SoecoImageList } from "./SoecoImageList";
import Div100vh from "react-div-100vh";
import { SoecoButton } from "../../components/SoecoButton";
import { Camera } from "lucide-react";
import { SoecoHeader } from "../../components/SoecoHeader";
import { Card, CardActions } from "@mui/material";

type Props = {
    error: boolean;
    errors: ErrorRecord<Product>;
    handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    images: File[];
    setImages: (images: File[]) => void;
    imageUrls: string[];
    setImageUrls: (imageUrls: string[]) => void;
    newImages: File[];
    setNewImages: (newImages: File[]) => void;
    newImageUrls: string[];
    combinedImageUrls: string[];
    changedProduct: Product;
    setChangedProduct: (product: Product) => void;
};

export const ExistingImageList = (props: Props) => {
    const { companyId, inventoryId, productId } = useParams();
    const productQuery = useGetProduct(companyId ?? "", inventoryId ?? "", productId ?? "");
    const snackBar = useSnackBar();
    const [imageSrc, setImageSrc] = React.useState("");
    const [crop, setCrop] = React.useState<Point>({ x: 0, y: 0 });
    const [zoom, setZoom] = React.useState(1);
    const rotation = 0;
    const [croppedAreaPixels, setCroppedAreaPixels] = React.useState({} as Area);
    const [croppedImage, setCroppedImage] = React.useState("");
    useEffect(() => {
        // iPhone images from an iPhone 14 Pro Max is still very large even with compression
        // What is the image format that is used by the iPhone?
        // Is smaller versions of the image created in the backend?
        const img = new Image();
        img.src = croppedImage;
        img.onload = () => {
            URL.revokeObjectURL(img.src);
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx && ctx.drawImage(img, 0, 0);
            canvas.toBlob(
                blob => {
                    props.setImages([...props.images, blob as File]);
                    props.setNewImages([...props.newImages, blob as File]);
                },
                "image/jpeg",
                0.7,
            );
        };
        setZoom(1);
        setCrop({ x: 0, y: 0 });
        setImageSrc("");
    }, [croppedImage]);

    const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const showCroppedImage = useCallback(() => {
        getCroppedImg(imageSrc, croppedAreaPixels, rotation)
            .then(croppedImage => {
                setCroppedImage(croppedImage);
            })
            .catch(e => {
                console.error(e);
            });
    }, [imageSrc, croppedAreaPixels, rotation]);

    const onClose = useCallback(() => {
        setCroppedImage("");
        setImageSrc("");
    }, []);

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file: File = e.target.files[0];
            readFile(file, (result: string) => {
                setImageSrc(result);
            });
        }
    };

    return productQuery.isLoading || !productQuery.data ? (
        <Loader />
    ) : productQuery.isError ? (
        <div>Error</div>
    ) : (
        <CameraInputWrapper>
            {imageSrc != "" ? (
                <>
                    <CropContainerWrapper>
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={4 / 3}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                        />
                    </CropContainerWrapper>
                    <ButtonWrapper>
                        <SoecoButton fullWidth size="large" color="warning" onClick={onClose}>
                            Cancel
                        </SoecoButton>
                        <SoecoButton fullWidth size="large" onClick={showCroppedImage}>
                            Save
                        </SoecoButton>
                    </ButtonWrapper>
                </>
            ) : (
                <>
                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: "30px",
                            width: "90%",
                            overflowY: "auto",
                            maxHeight: "80vh",
                        }}
                    >
                        {" "}
                        <StyledCardActions>
                            <IconActionsWrapper>
                                <StyledLabel htmlFor="upload-image-button">
                                    <Camera size={24} /> Upload Image
                                </StyledLabel>

                                <StyledInput
                                    type="file"
                                    id="upload-image-button"
                                    name="image"
                                    accept="image/*"
                                    onChange={onFileChange}
                                />
                                {props.error ? <p>{props.errors.imageUrls}</p> : null}
                            </IconActionsWrapper>
                        </StyledCardActions>
                        <SoecoImageList
                            images={props.images}
                            setImages={props.setImages}
                            imageUrls={props.combinedImageUrls}
                            setImageUrls={props.setImageUrls}
                            error={props.error}
                            errors={props.errors}
                            newImages={props.newImages}
                            setNewImages={props.setNewImages}
                            combinedImageUrls={props.combinedImageUrls}
                            changedProduct={props.changedProduct}
                            setChangedProduct={props.setChangedProduct}
                        />
                    </Card>
                </>
            )}
        </CameraInputWrapper>
    );
};

const CameraInputWrapper = styled(Div100vh)`
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
`;

const ButtonWrapper = styled.div`
    position: absolute;
    display: flex;
    width: 100%;
    //gap: 1rem;
    bottom: 0;
    z-index: 1;
    margin-bottom: 36px;
`;

const CropContainerWrapper = styled.div`
    display: flex;
`;

const StyledInput = styled.input`
    display: none;
`;

const StyledLabel = styled.label`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    font-family: "Poppins", sans-serif;
    color: #8c8c8c;
    font-size: 18px;
    font-weight: 600;
    line-height: 24px;
`;

const ImageListWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-left: 15px;
    margin-right: 15px;
    margin-top: 15px;
    align-items: center;
`;
const IconActionsWrapper = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: center;
`;
const StyledCardActions = styled(CardActions)`
    display: flex;
    width: 100%;
    font-size: 3rem;
    border-bottom: 1px solid #ebebeb;
    //background-color: #ebebeb;
    align-items: center; /* Centrerar vertikalt i flex-container */
`;
